import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { FileText, Download, Eye, ExternalLink, Folder, Trees, Mail, Phone } from "lucide-react";

const documentLibrary = [
  {
    id: 1,
    title: "Park Master Plan Overview",
    type: "PDF",
    size: "2.4 MB",
    downloads: 1456,
    lastUpdated: "2024-08-15",
    category: "Planning Documents",
    status: "Final",
    description: "Comprehensive overview of the proposed park improvements and community vision"
  },
  {
    id: 2,
    title: "Community Survey Summary",
    type: "PDF",
    size: "1.8 MB",
    downloads: 1234,
    lastUpdated: "2024-08-20",
    category: "Community Input",
    status: "Final",
    description: "Summary of community survey responses and key feedback themes"
  },
  {
    id: 3,
    title: "Site Analysis Report",
    type: "PDF",
    size: "5.2 MB",
    downloads: 867,
    lastUpdated: "2024-07-30",
    category: "Technical Studies",
    status: "Final",
    description: "Detailed analysis of existing park conditions and infrastructure"
  },
  {
    id: 4,
    title: "Concept Design Plans",
    type: "PDF",
    size: "8.7 MB",
    downloads: 789,
    lastUpdated: "2024-08-18",
    category: "Design Documents",
    status: "Draft",
    description: "Initial concept designs for park improvements based on community input"
  },
  {
    id: 5,
    title: "Environmental Assessment",
    type: "PDF",
    size: "6.3 MB",
    downloads: 634,
    lastUpdated: "2024-08-10",
    category: "Environmental",
    status: "Draft",
    description: "Environmental impact assessment and sustainability considerations"
  },
  {
    id: 6,
    title: "Public Meeting Presentations",
    type: "PDF",
    size: "3.5 MB",
    downloads: 567,
    lastUpdated: "2024-07-25",
    category: "Community Engagement",
    status: "Final",
    description: "Presentations from community workshops and public information sessions"
  }
];



const externalLinks = [
  {
    title: "Local Council Website",
    url: "https://council.gov.au/parks",
    description: "Official council information about parks and recreation planning"
  },
  {
    title: "Australian Standards for Playgrounds",
    url: "https://standards.org.au/playground",
    description: "Safety standards and guidelines for playground equipment and design"
  },
  {
    title: "Disability Discrimination Act Guidelines",
    url: "https://humanrights.gov.au/disability",
    description: "Accessibility requirements for public facilities and spaces"
  },
  {
    title: "Environmental Protection Authority",
    url: "https://epa.gov.au",
    description: "Environmental regulations and sustainability guidelines"
  },
  {
    title: "Parks and Recreation Australia",
    url: "https://parksaustralia.gov.au",
    description: "Best practices and resources for park planning and management"
  },
  {
    title: "Crime Prevention Through Design",
    url: "https://cpted.gov.au",
    description: "Guidelines for designing safe and secure public spaces"
  }
];

const upcomingEvents = [
  {
    title: "Community Design Workshop",
    date: "2024-09-15",
    time: "10:00 AM - 2:00 PM",
    location: "Community Centre, Main Hall",
    description: "Interactive workshop to review and refine concept designs",
    type: "Workshop",
    capacity: "50 people"
  },
  {
    title: "Public Information Session",
    date: "2024-09-22",
    time: "7:00 PM - 9:00 PM", 
    location: "Local Library, Meeting Room 1",
    description: "Q&A session about the master plan proposals",
    type: "Information Session",
    capacity: "80 people"
  },
  {
    title: "Final Community Consultation",
    date: "2024-10-30",
    time: "6:00 PM - 8:00 PM",
    location: "School Hall, Elm Street",
    description: "Present final designs before council submission",
    type: "Consultation",
    capacity: "150 people"
  }
];

const contactMethods = [
  {
    method: "Email",
    contact: "parkplan@council.gov.au",
    description: "Send us your questions or feedback via email",
    icon: Mail
  },
  {
    method: "Phone",
    contact: "(02) 9123-4567",
    description: "Speak directly with the project team",
    icon: Phone
  }
];

const getStatusBadgeColor = (status) => {
  switch (status) {
    case "Final":
      return "bg-green-100 text-green-800 hover:bg-green-100";
    case "Draft":
      return "bg-yellow-100 text-yellow-800 hover:bg-yellow-100";
    case "In Review":
      return "bg-blue-100 text-blue-800 hover:bg-blue-100";
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-100";
  }
};

export function ResourcesTab() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1>Resources & Information</h1>
          <p className="text-muted-foreground mt-1">
            Access project documents, team contacts, upcoming events, and helpful resources
          </p>
        </div>
        <Badge variant="secondary" className="flex items-center gap-1">
          <Trees className="h-3 w-3" />
          Park Planning Resources
        </Badge>
      </div>

      {/* Document Library */}
      <Card>
        <CardHeader>
          <CardTitle>Document Library</CardTitle>
          <CardDescription>Download project documents, reports, and planning materials</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {documentLibrary.map((doc) => (
              <div key={doc.id} className="p-4 border rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <h3 className="font-medium">{doc.title}</h3>
                    <Badge 
                      variant="secondary" 
                      className={getStatusBadgeColor(doc.status)}
                    >
                      {doc.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm">
                      <Eye className="h-4 w-4" />
                      View
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Download className="h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground">{doc.description}</p>
                
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center gap-4">
                    <span>Type: {doc.type}</span>
                    <span>Size: {doc.size}</span>
                    <span>Category: {doc.category}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Download className="h-3 w-3" />
                      {doc.downloads} downloads
                    </span>
                    <span>Updated: {new Date(doc.lastUpdated).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Events */}
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Events</CardTitle>
          <CardDescription>Community meetings and consultation opportunities</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {upcomingEvents.map((event, index) => {
              const daysUntil = Math.ceil((new Date(event.date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
              const isUpcoming = daysUntil > 0;
              
              return (
                <div key={index} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <h3 className="font-medium">{event.title}</h3>
                      <Badge variant="outline" className="text-xs">
                        {event.type}
                      </Badge>
                    </div>
                    {isUpcoming && (
                      <Badge variant="secondary" className="text-xs">
                        {daysUntil === 1 ? "Tomorrow" : `${daysUntil} days`}
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-sm text-muted-foreground">{event.description}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Date & Time:</span>
                      <div className="text-muted-foreground">
                        {new Date(event.date).toLocaleDateString()}
                      </div>
                      <div className="text-muted-foreground">{event.time}</div>
                    </div>
                    <div>
                      <span className="font-medium">Location:</span>
                      <div className="text-muted-foreground">{event.location}</div>
                      <div className="text-muted-foreground">Capacity: {event.capacity}</div>
                    </div>
                  </div>
                  
                  <Button variant="outline" size="sm" className="w-full mt-2">
                    Register Interest
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Us</CardTitle>
          <CardDescription>Get in touch with the project team</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {contactMethods.map((contact, index) => {
              const Icon = contact.icon;
              return (
                <div key={index} className="p-4 border rounded-lg text-center space-y-2">
                  <Icon className="h-6 w-6 mx-auto text-blue-600" />
                  <h4 className="font-medium">{contact.method}</h4>
                  <p className="text-sm font-mono">{contact.contact}</p>
                  <p className="text-xs text-muted-foreground">{contact.description}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* How to Stay Informed */}
      {/* <Card>
        <CardHeader>
          <CardTitle>Stay Informed</CardTitle>
          <CardDescription>Keep up to date with project progress and opportunities to participate</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 border rounded-lg space-y-2">
              <h4 className="font-medium">Email Updates</h4>
              <p className="text-sm text-muted-foreground">
                Subscribe to receive project updates, meeting notifications, and survey invitations
              </p>
              <Button variant="outline" size="sm">Subscribe to Updates</Button>
            </div>
            
            <div className="p-4 border rounded-lg space-y-2">
              <h4 className="font-medium">Council Website</h4>
              <p className="text-sm text-muted-foreground">
                Visit the council website for the latest news and project announcements
              </p>
              <Button variant="outline" size="sm">Visit Council Site</Button>
            </div>
          </div>
        </CardContent>
      </Card> */}
    </div>
  );
}